import type { ApiResponse } from '@alpha-edge/shared'

const BASE_URL = '/api'

let authToken: string | null = null

export function setAuthToken(token: string | null): void {
  authToken = token
}

export function getAuthToken(): string | null {
  return authToken
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export class NetworkError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'NetworkError'
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<ApiResponse<T>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`
  }

  let response: Response
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    })
  } catch {
    throw new NetworkError('Network request failed')
  }

  const data = (await response.json()) as ApiResponse<T>

  if (!response.ok) {
    throw new ApiError(data.error ?? 'Request failed', response.status, data.code)
  }

  return data
}

// Multipart variant used by the single upload caller (OrgSettingsPage
// branding logo in §7.5.2). Separate from `request()` because it must
// let fetch derive the Content-Type boundary header from the FormData;
// setting it manually corrupts the multipart envelope.
async function requestForm<T>(path: string, form: FormData): Promise<ApiResponse<T>> {
  const headers: Record<string, string> = {}
  if (authToken) headers['Authorization'] = `Bearer ${authToken}`

  let response: Response
  try {
    response = await fetch(`${BASE_URL}${path}`, { method: 'POST', headers, body: form })
  } catch {
    throw new NetworkError('Network request failed')
  }
  const data = (await response.json()) as ApiResponse<T>
  if (!response.ok) throw new ApiError(data.error ?? 'Request failed', response.status, data.code)
  return data
}

export interface BlobResult {
  readonly blob: Blob
  readonly filename: string | null
}

function filenameFromContentDisposition(disposition: string | null): string | null {
  if (!disposition) return null
  const match = /filename="([^"]+)"/.exec(disposition)
  return match ? match[1] : null
}

// Raw/blob variant used by file-download callers (label template export in
// §7.6.22 Slice 2). Separate from `request()` because `request()` always
// JSON-parses the response body, which would corrupt a binary/text payload.
async function requestBlob(path: string): Promise<BlobResult> {
  const headers: Record<string, string> = {}
  if (authToken) headers['Authorization'] = `Bearer ${authToken}`

  let response: Response
  try {
    response = await fetch(`${BASE_URL}${path}`, { method: 'GET', headers })
  } catch {
    throw new NetworkError('Network request failed')
  }

  if (!response.ok) {
    const data = (await response.json().catch(() => ({}) as ApiResponse<unknown>)) as ApiResponse<unknown>
    throw new ApiError(data.error ?? 'Request failed', response.status, data.code)
  }

  const blob = await response.blob()
  const filename = filenameFromContentDisposition(response.headers.get('Content-Disposition'))
  return { blob, filename }
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
  postForm: <T>(path: string, form: FormData) => requestForm<T>(path, form),
  getBlob: (path: string) => requestBlob(path),
}
